import { useRef, useState } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import { adminApi } from "./api";

export const inputCls =
  "w-full bg-obsidian/70 border border-woodborder focus:border-burnt text-cream text-sm px-4 py-3 outline-none transition-colors placeholder:text-clay rounded-sm";
export const labelCls = "block font-mono text-[10px] tracking-[0.22em] text-tan mb-1.5";

export const AField = ({ label, children, testId }) => (
  <div data-testid={testId}>
    <label className={labelCls}>{label}</label>
    {children}
  </div>
);

const BTN_STYLES = {
  primary: "bg-burnt hover:bg-burnt-hover text-cream",
  gold: "bg-gold hover:bg-gold-bright text-obsidian font-bold",
  ghost: "border border-woodborder hover:border-tan/50 text-sand hover:text-cream",
  danger: "border border-red-900/60 text-red-400 hover:bg-red-900/20",
};

export const Btn = ({ variant = "primary", className = "", ...props }) => (
  <button
    {...props}
    className={`inline-flex items-center justify-center gap-2 font-mono text-[11px] tracking-[0.18em] px-4 py-2.5 rounded-full transition-colors disabled:opacity-50 ${BTN_STYLES[variant]} ${className}`}
  />
);

const PILL_STYLES = {
  published: "bg-gold/15 text-gold-bright border-gold/40",
  draft: "bg-clay/15 text-sand border-clay/40",
  New: "bg-burnt/15 text-burnt-hover border-burnt/40",
  Reviewing: "bg-tan/15 text-tan-light border-tan/40",
  Contacted: "bg-gold/15 text-gold-bright border-gold/40",
  Closed: "bg-clay/15 text-clay border-clay/40",
};

export const StatusPill = ({ status }) => (
  <span className={`font-mono text-[9px] tracking-[0.18em] px-2.5 py-1 rounded-full border ${PILL_STYLES[status] || PILL_STYLES.draft}`}>
    {(status || "draft").toUpperCase()}
  </span>
);

export const Uploader = ({ onUploaded, accept = "image/*", label = "UPLOAD FILE", testId }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await adminApi.post("/admin/upload", fd);
      onUploaded(data.path);
    } catch {
      setErr("Upload failed. Try again.");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={pick} data-testid={testId ? `${testId}-file` : undefined} />
      <Btn type="button" variant="ghost" onClick={() => ref.current?.click()} disabled={busy} data-testid={testId}>
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
        {busy ? "UPLOADING..." : label}
      </Btn>
      {err && <p className="mt-1.5 font-mono text-[10px] text-red-400">{err}</p>}
    </div>
  );
};

export const Toggle = ({ checked, onChange, label, testId }) => (
  <button type="button" onClick={() => onChange(!checked)} className="flex items-center gap-3" data-testid={testId}>
    <span className={`w-10 h-6 rounded-full relative transition-colors shrink-0 ${checked ? "bg-burnt" : "bg-woodborder"}`}>
      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-cream transition-all ${checked ? "left-[18px]" : "left-0.5"}`} />
    </span>
    <span className="font-mono text-[10px] tracking-[0.18em] text-sand text-left">{label}</span>
  </button>
);

export const PageHead = ({ title, sub, children }) => (
  <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
    <div>
      <h1 className="font-display text-4xl md:text-5xl text-cream tracking-wide">{title}</h1>
      {sub && <p className="mt-2 text-clay text-sm font-light">{sub}</p>}
    </div>
    {children && <div className="flex flex-wrap gap-3">{children}</div>}
  </div>
);

export const Modal = ({ open, onClose, title, children, wide }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center p-4 overflow-y-auto" data-testid="admin-modal">
      <div className="absolute inset-0 bg-obsidian/85 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative bg-wood border border-woodborder w-full ${wide ? "max-w-3xl" : "max-w-xl"} my-8`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-woodborder">
          <h3 className="font-display text-2xl text-cream tracking-wide">{title}</h3>
          <button onClick={onClose} className="text-sand hover:text-cream p-1" data-testid="admin-modal-close" aria-label="Close">
            <X size={20} />
          </button>
        </div>
        <div className="p-6 max-h-[75vh] overflow-y-auto">{children}</div>
      </div>
    </div>
  );
};

export const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
